import { useState } from 'react';
import { Modal, Input, Button, Message, Typography } from '@arco-design/web-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { endpoints } from '../../api/endpoints';

interface PhoneVerificationDialogProps {
  visible: boolean;
  phoneNumber: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export const PhoneVerificationDialog = ({
  visible,
  phoneNumber,
  onSuccess,
  onCancel,
}: PhoneVerificationDialogProps) => {
  const { t } = useTranslation();
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      Message.error(t('sms.verification_code_invalid'));
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(endpoints.sms.checkVerification, {
        phone_number: phoneNumber,
        code: verificationCode,
      });

      if (response.data.status === 'verified') {
        Message.success(t('sms.phone_verified_success'));
        onSuccess();
        setVerificationCode('');
      }
    } catch (error: any) {
      console.error('Phone verification error:', error);
      const errorData = error.response?.data;
      if (errorData?.phone_already_in_use) {
        Message.error(t('sms.phone_already_in_use'));
        return;
      }
      const errorMessage = errorData?.error || t('sms.verification_failed');
      const twilioCode = errorData?.twilio_code;
      Message.error(twilioCode ? `${errorMessage} (Twilio ${twilioCode})` : errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setVerificationCode('');
    onCancel();
  };

  return (
    <Modal
      title={t('sms.verify_phone_title')}
      visible={visible}
      onCancel={handleCancel}
      footer={null}
      style={{ width: 400 }}
    >
      <div style={{ padding: '20px 0' }}>
        <Typography.Paragraph>
          {t('sms.verification_code_sent')} <strong>{phoneNumber}</strong>
        </Typography.Paragraph>
        
        <Typography.Paragraph style={{ color: '#888', fontSize: '14px' }}>
          {t('sms.verification_code_instructions')}
        </Typography.Paragraph>

        <Input
          placeholder={t('sms.verification_code_placeholder')}
          value={verificationCode}
          onChange={setVerificationCode}
          maxLength={6}
          style={{ fontSize: '24px', letterSpacing: '8px', textAlign: 'center', marginTop: 20 }}
          onPressEnter={handleVerify}
          autoFocus
        />

        <div style={{ marginTop: 20, display: 'flex', gap: 10 }}>
          <Button onClick={handleCancel} style={{ flex: 1 }}>
            {t('sms.cancel_change_phone_button')}
          </Button>
          <Button
            type="primary"
            onClick={handleVerify}
            loading={loading}
            disabled={verificationCode.length !== 6}
            style={{ flex: 1 }}
          >
            {t('sms.verify_button')}
          </Button>
        </div>

        <Typography.Paragraph style={{ marginTop: 20, fontSize: '12px', color: '#888' }}>
          {t('sms.code_expires_in_10_min')}
        </Typography.Paragraph>
      </div>
    </Modal>
  );
};

